import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useProviderType from '@app/src/hooks/useProviderType'
import { useFetchAssessmentSettings } from '@app/src/pages/Assessments/useFetchAssessmentSettings'
import { useFetchExpectationAutomation } from '@app/src/pages/Expectation/useExpectationData'
import {
  getDefaultTargetLevel,
  getExpectationAssessmentTemplates,
  pickInitialAssessmentTemplateId,
} from '@app/src/pages/ResourceCollection/Collections/Provider/BulkCreateExpectation/bulkCreateExpectationLogic'
import SelectAssessmentStep from '@app/src/pages/ResourceCollection/Collections/Provider/BulkCreateExpectation/Steps/SelectAssessmentStep'
import SetTargetStep from '@app/src/pages/ResourceCollection/Collections/Provider/BulkCreateExpectation/Steps/SetTargetStep'
import type { ExpectationAutomation } from '@app/src/types/expectations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import { Dialog, Skeleton } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useImportantKpisFooterContent } from '../CreationSteps/HighlightImportantKpis/HighlightImportantKpis'
import { countSelectedKpis } from '../CreationSteps/HighlightImportantKpis/importantKpisLogic'
import useBaselineAssessmentTemplate from '../Hooks/useBaselineAssessmentTemplate'
import {
  buildDefaultFormValues,
  canSubmit,
  mapAutomationToFormData,
  withEditedAutomationTemplate,
} from './expectationAutomationLogic'
import { ExpectationSummaryStep } from './Steps/ExpectationSummaryStep'
import HighlightImportantKpisStep from './Steps/HighlightImportantKpisStep'
import SetDeadlineStep from './Steps/SetDeadlineStep'
import SetRiskStatusStep from './Steps/SetRiskStatusStep'
import type { ExpectationAutomationFormData } from './types'
import { ExpectationAutomationFormField } from './types'
import useExpectationAffectedCount from './useExpectationAffectedCount'
import useSaveExpectationAutomation from './useSaveExpectationAutomation'

interface CreateExpectationAutomationModalProps {
  open: boolean
  onClose: () => void
  editedExpectationAutomation?: ExpectationAutomation
  onSaved?: () => void | Promise<void>
}

type Question = {
  title: string
  description?: React.ReactNode
  fieldnames: ExpectationAutomationFormField[]
  children: React.ReactNode
  canGoNext?: (values: unknown) => boolean
  hasSkipButton?: boolean
  customOnClickSkip?: () => void
  footerContent?: React.ReactNode
  overrideButtonTextId?: string
  disableInitialBack?: boolean
}

const ImportantKpisFooterContent: React.FC = () => {
  const content = useImportantKpisFooterContent(ExpectationAutomationFormField.ImportantKpis)
  return <>{content}</>
}

const CreateExpectationAutomationModal: React.FC<CreateExpectationAutomationModalProps> = ({
  open,
  onClose,
  editedExpectationAutomation,
  onSaved,
}) => {
  const { formatMessage } = useIntl()
  const providerType = useProviderType()
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { showSnackbar } = useSnackbar()
  const { trackEvent } = useAmplitude()
  const { saveExpectationAutomation, isExpectationAutomationLoading } = useSaveExpectationAutomation()
  const { multiAssessmentExpectation } = useFlags()
  const { baselineAssessmentTemplate, isLoading: isLoadingBaselineTemplate } = useBaselineAssessmentTemplate()
  const { assessmentSettings, isLoading: isLoadingAssessmentSettings } = useFetchAssessmentSettings()

  const formMethods = useForm<ExpectationAutomationFormData>({
    shouldUnregister: false,
    defaultValues: buildDefaultFormValues(),
  })
  const { handleSubmit, reset, setValue, getValues, watch } = formMethods

  // Subscribe so canGoNext re-evaluates on changes.
  const selectedTemplateId = watch(ExpectationAutomationFormField.AssessmentTemplateId)
  watch(ExpectationAutomationFormField.DeadlineType)
  watch(ExpectationAutomationFormField.CustomDueDate)
  watch(ExpectationAutomationFormField.ApplyToExisting)

  const editedId = editedExpectationAutomation?.id
  const { data: fetchedAutomation } = useFetchExpectationAutomation(open ? editedId : undefined)

  useEffect(() => {
    if (!open) {
      setActiveStep(1)
      reset(buildDefaultFormValues())
      return
    }
    if (fetchedAutomation) {
      reset(mapAutomationToFormData(fetchedAutomation))
    } else if (editedExpectationAutomation) {
      reset(mapAutomationToFormData(editedExpectationAutomation))
    } else {
      reset(buildDefaultFormValues())
    }
  }, [open, editedId, fetchedAutomation])

  const assessmentTemplates = useMemo(
    () =>
      withEditedAutomationTemplate(
        getExpectationAssessmentTemplates(
          baselineAssessmentTemplate,
          assessmentSettings,
          undefined,
          Boolean(multiAssessmentExpectation),
        ),
        editedExpectationAutomation?.assessmentTemplate,
      ),
    [baselineAssessmentTemplate, assessmentSettings, editedExpectationAutomation, multiAssessmentExpectation],
  )
  const selectedTemplate = assessmentTemplates.find(t => t.id === selectedTemplateId)

  const selectTemplate = (template: AssessmentTemplate) => {
    if (template.id === getValues(ExpectationAutomationFormField.AssessmentTemplateId)) return
    setValue(ExpectationAutomationFormField.AssessmentTemplateId, template.id)
    setValue(ExpectationAutomationFormField.TargetAssessmentLevel, getDefaultTargetLevel(template.levels))
    // Highlighted KPIs are questions of the previous template
    setValue(ExpectationAutomationFormField.ImportantKpis, [])
  }

  // New automations start on the first offered template; edited ones keep their own.
  useEffect(() => {
    if (!open || editedExpectationAutomation || selectedTemplateId != null) return
    // Choosing before the settings load would settle on a partial list, often only Baseline
    if (multiAssessmentExpectation && isLoadingAssessmentSettings) return
    const initialTemplateId = pickInitialAssessmentTemplateId(assessmentTemplates, undefined)
    const initialTemplate = assessmentTemplates.find(t => t.id === initialTemplateId)
    if (initialTemplate) selectTemplate(initialTemplate)
  }, [
    open,
    editedExpectationAutomation,
    selectedTemplateId,
    assessmentTemplates,
    multiAssessmentExpectation,
    isLoadingAssessmentSettings,
  ])

  const { affectedCount, isLoadingCount } = useExpectationAffectedCount(editedId, selectedTemplateId)

  const selectAssessmentQuestion: Question = {
    title: formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.title' }),
    description: formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.description' }, { providerType }),
    fieldnames: [],
    children: (
      <SelectAssessmentStep
        assessmentTemplates={assessmentTemplates}
        selectedTemplateId={selectedTemplateId}
        onSelect={selectTemplate}
        showComingSoon={false}
      />
    ),
    canGoNext: () => getValues(ExpectationAutomationFormField.AssessmentTemplateId) != null,
    disableInitialBack: true,
  }

  const QUESTIONS: Question[] = [
    ...(multiAssessmentExpectation ? [selectAssessmentQuestion] : []),
    {
      title: formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.title' }),
      description: formatMessage({ id: 'bulkCreateExpectation.steps.setTarget.description' }, { providerType }),
      fieldnames: [ExpectationAutomationFormField.TargetAssessmentLevel],
      children: <SetTargetStep assessmentTemplate={selectedTemplate} />,
      disableInitialBack: !multiAssessmentExpectation,
    },
    {
      title: formatMessage({ id: 'form.createExpectationAutomation.riskStatus.title' }, { providerType }),
      description: formatMessage({ id: 'form.createExpectationAutomation.riskStatus.description' }, { providerType }),
      fieldnames: [ExpectationAutomationFormField.HighestAllowedProviderRiskStatus],
      children: <SetRiskStatusStep />,
    },
    {
      title: formatMessage({ id: 'form.createExpectationAutomation.deadline.title' }),
      description: formatMessage({ id: 'form.createExpectationAutomation.deadline.description' }),
      fieldnames: [ExpectationAutomationFormField.DeadlineType, ExpectationAutomationFormField.CustomDueDate],
      children: <SetDeadlineStep />,
      canGoNext: values => canSubmit(values as ExpectationAutomationFormData),
    },
    {
      title: formatMessage({ id: 'form.createAutomation.highlightImportantKpis.title' }),
      description: formatMessage({ id: 'form.createAutomation.highlightImportantKpis.description' }, { providerType }),
      fieldnames: [],
      children: <HighlightImportantKpisStep assessmentTemplateId={selectedTemplateId ?? undefined} />,
      hasSkipButton: true,
      customOnClickSkip: () => {
        setValue(ExpectationAutomationFormField.ImportantKpis, [])
        setActiveStep(current => current + 1)
      },
      footerContent: <ImportantKpisFooterContent />,
    },
    {
      title: formatMessage({ id: 'form.createAutomation.summary.title' }),
      description: isLoadingCount ? <Skeleton variant="text" /> : null,
      fieldnames: [],
      children: (
        <ExpectationSummaryStep
          affectedCount={affectedCount}
          isLoadingCount={isLoadingCount}
          bannerMessageId="form.createAutomation.applyTo.immediateExpectationBanner"
          importantKpisCount={countSelectedKpis(getValues(ExpectationAutomationFormField.ImportantKpis) ?? [])}
          isEditing={Boolean(editedExpectationAutomation)}
          assessmentTemplate={selectedTemplate}
        />
      ),
      overrideButtonTextId: editedExpectationAutomation
        ? 'form.createAutomation.summary.saveAutomation'
        : 'form.createAutomation.summary.finalizeAutomation',
    },
  ]

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
  }, [QUESTIONS.length])

  const activeQuestion = QUESTIONS[activeStep - 1]

  const onSubmit = async (values: ExpectationAutomationFormData) => {
    const assessmentTemplateId = values[ExpectationAutomationFormField.AssessmentTemplateId]
    if (assessmentTemplateId == null) throw new Error('No assessment template selected')

    await saveExpectationAutomation(values, assessmentTemplateId, () => {
      if (!editedExpectationAutomation) {
        trackEvent({
          name: AmplitudeTrackingEvents.Accessor.Expectation.AutomationCreated,
          eventProps: {
            assessment_template_id: assessmentTemplateId,
            target_assessment_level: values[ExpectationAutomationFormField.TargetAssessmentLevel],
            highest_allowed_provider_risk_status:
              values[ExpectationAutomationFormField.HighestAllowedProviderRiskStatus],
            deadline_type: values[ExpectationAutomationFormField.DeadlineType],
            important_kpis_count: countSelectedKpis(values[ExpectationAutomationFormField.ImportantKpis] ?? []),
            affected_count: affectedCount,
          },
        })
      }
      showSnackbar({
        message: formatMessage({ id: 'notifications.automationCreated' }),
        severity: NotificationSeverity.success,
        disableAutoClose: true,
      })
      onSaved?.()
      onClose()
    })
  }

  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      <CreationModalContainer
        title={formatMessage({ id: 'form.createExpectationAutomation.modalTitle' })}
        onClose={onClose}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeQuestion && (
              <CreationModalStep
                title={activeQuestion.title}
                description={activeQuestion.description}
                fieldnames={activeQuestion.fieldnames}
                onBack={() => undefined}
                hasSkipButton={activeQuestion.hasSkipButton}
                customOnClickSkip={activeQuestion.customOnClickSkip}
                footerContent={activeQuestion.footerContent}
                overrideButtonTextId={activeQuestion.overrideButtonTextId}
                canGoNext={activeQuestion.canGoNext}
                disableInitialBack={activeQuestion.disableInitialBack}
                isLoading={
                  isExpectationAutomationLoading ||
                  isLoadingBaselineTemplate ||
                  (Boolean(multiAssessmentExpectation) && isLoadingAssessmentSettings)
                }
              >
                {activeQuestion.children}
              </CreationModalStep>
            )}
          </form>
        </FormProvider>
      </CreationModalContainer>
    </Dialog>
  )
}

export default CreateExpectationAutomationModal
